import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { calendarApi, integrationKey } from '../../lib/calendar-api';
import { IntegrationBar } from './IntegrationBar';
import { CalendarCard } from './CalendarCards';
import { ChatEmailCard } from '../email/Emails';
export function IntegrationGroup({ resultSetId }: { resultSetId: string }) {
  const client = useQueryClient();
  const query = useInfiniteQuery({
    queryKey: [...integrationKey(), 'results', resultSetId],
    queryFn: ({ pageParam }) => calendarApi.results(resultSetId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
    retry: false,
    refetchInterval: 5000,
  });
  const refresh = useMutation({
    mutationFn: () => calendarApi.refreshResults(resultSetId),
    onSuccess: () => client.invalidateQueries({ queryKey: integrationKey() }),
  });
  const page = query.data?.pages[0];
  if (!page)
    return (
      <p role="status">
        {query.isError ? 'These results are unavailable.' : 'Loading results…'}
      </p>
    );
  const status = page.scanStatus;
  return (
    <IntegrationBar
      provider={page.provider}
      title={page.title}
      summary={
        status
          ? ['queued', 'running'].includes(status)
            ? 'Scanning…'
            : 'Scan could not complete. Refresh to retry.'
          : `${page.totalCount} ${page.provider === 'gmail' ? 'emails' : 'meetings'}`
      }
      action={
        <button disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          Refresh results
        </button>
      }
    >
      <p>Results from {new Date(page.createdAt).toLocaleString()}</p>
      {!page.totalCount && !status && (
        <p>Nothing to review. Click Refresh results to rescan this query.</p>
      )}
      {query.data?.pages
        .flatMap((p) => p.items)
        .map((item) =>
          !item.available ? (
            <p key={item.id}>This item is unavailable.</p>
          ) : page.provider === 'gmail' ? (
            <ChatEmailCard key={item.id} threadId={item.id} />
          ) : (
            <CalendarCard key={item.id} eventId={item.id} />
          ),
        )}
      {query.hasNextPage && (
        <button
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          More results
        </button>
      )}
      {refresh.error && <p role="alert">{refresh.error.message}</p>}
    </IntegrationBar>
  );
}
